import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Combobox, ComboboxInput, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { MagnifyingGlassIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import EventItem, { EventItemHeader } from './EventItem';
import { Link, useSearchParams } from 'react-router-dom';
import {
  fetchEventsPaginated,
  selectPaginatedEvents,
  selectPaginatedPage,
  selectPaginatedRequestStatus,
  selectPaginatedTotalPages,
} from '../../reducers/eventsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useToken } from '../../hooks/useToken';
import { getConfig } from '../../config/config-helper';
import { ClockIcon } from '@heroicons/react/16/solid';

// Filter types
type StatusFilter = 'for-review' | 'rejected' | 'ready' | 'pending' | 'decommissioned';
type SpecialFilter = 'starting-soon' | 'decommissioning-soon';
type FilterType = StatusFilter | SpecialFilter;

const STATUS_FILTERS: StatusFilter[] = ['for-review', 'rejected', 'ready', 'pending', 'decommissioned'];

interface FilterButtonProps {
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, icon = undefined, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center px-2 py-0.5 gap-1 rounded-sm text-sm font-medium transition-colors ${
        isActive ? 'bg-dataops-rich-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {icon && <div>{icon}</div>}
      <div>{label}</div>
    </button>
  );
};

// Filter configuration
const FILTER_CONFIG: Array<{
  key: FilterType;
  label: string;
  icon?: React.ReactNode;
}> = [
  {
    key: 'for-review',
    label: 'For Review',
    icon: <div className="h-2 w-2 bg-dataops-saffron-yellow rounded-full" />,
  },
  { key: 'rejected', label: 'Rejected', icon: <div className="h-2 w-2 bg-dataops-crayola-red rounded-full" /> },
  { key: 'ready', label: 'Ready', icon: <div className="h-2 w-2 bg-dataops-forest-green rounded-full" /> },
  { key: 'pending', label: 'Pending', icon: <div className="h-2 w-2 bg-dataops-taupe-gray rounded-full" /> },
  {
    key: 'decommissioned',
    label: 'Decommissioned',
    icon: <div className="h-2 w-2 bg-dataops-rich-black rounded-full" />,
  },
  { key: 'starting-soon', label: 'Starting Soon', icon: <ClockIcon className="h-3 w-3 text-dataops-amethyst-lilac" /> },
  {
    key: 'decommissioning-soon',
    label: 'Decommissioning Soon',
    icon: <ClockIcon className="h-3 w-3 text-dataops-amethyst-lilac" />,
  },
];

export default function EventsList() {
  const dispatch = useAppDispatch();
  const token = useToken();
  const paginatedEvents = useAppSelector(selectPaginatedEvents);
  const paginatedRequestStatus = useAppSelector(selectPaginatedRequestStatus);
  const paginatedTotalPages: number = useAppSelector(selectPaginatedTotalPages);
  const paginatedPage: number = useAppSelector(selectPaginatedPage);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set());
  const [selectedOrgAccount, setSelectedOrgAccount] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const sentinelRef = useRef<HTMLDivElement>(null);

  const PAGE_SIZE = 100;

  // Get organization accounts from config
  const { snowflakeOrganizationAccounts } = getConfig();
  // Only use filter when there are multiple accounts configured
  const hasMultipleOrgAccounts = snowflakeOrganizationAccounts && snowflakeOrganizationAccounts.length > 1;

  // In single-account environments, always scope to the single configured account (preserving
  // pre-FLT-250 behaviour). In multi-account environments, drive from the user's selection
  // (null = "All Accounts" → omit param → backend returns events for all accounts).
  const effectiveOrgAccount: string | undefined = hasMultipleOrgAccounts
    ? selectedOrgAccount ?? undefined
    : snowflakeOrganizationAccounts?.[0] ?? undefined;

  // Initialize search value and filters from URL params (only on mount)
  useEffect(() => {
    const initialSearch = searchParams.get('search') ?? '';
    setSearchValue(initialSearch);
    setDebouncedSearchValue(initialSearch);

    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      const filters = filtersParam.split(',') as FilterType[];
      setActiveFilters(new Set(filters));
    }

    const orgAccountParam = searchParams.get('org_account');
    if (orgAccountParam && snowflakeOrganizationAccounts?.includes(orgAccountParam)) {
      setSelectedOrgAccount(orgAccountParam);
    }

    setIsInitialized(true);
  }, []); // eslint-disable-line

  // Debounce search value with 1000ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchValue]);

  // Compute API status filters (only the server-side ones)
  const apiStatusFilters = useMemo(() => {
    return Array.from(activeFilters).filter((f): f is StatusFilter => STATUS_FILTERS.includes(f as StatusFilter));
  }, [activeFilters]);

  // Build date range params for special filters
  const buildDateRangeParams = useCallback(() => {
    const now = new Date().toISOString();
    const dateParams: {
      buildDateTo?: string;
      startDateFrom?: string;
      endDateTo?: string;
      decommissionDateFrom?: string;
    } = {};

    if (activeFilters.has('starting-soon')) {
      dateParams.buildDateTo = now;
      dateParams.startDateFrom = now;
    }

    if (activeFilters.has('decommissioning-soon')) {
      dateParams.endDateTo = now;
      dateParams.decommissionDateFrom = now;
    }

    return dateParams;
  }, [activeFilters]);

  // Dispatch fetchEventsPaginated when search/filters/token change (reset to page 1)
  useEffect(() => {
    if (!isInitialized) return;
    if (!token) return;
    // Only trigger if search is empty or >= 3 chars
    if (debouncedSearchValue.length > 0 && debouncedSearchValue.length < 3) return;

    const dateRangeParams = buildDateRangeParams();

    dispatch(
      fetchEventsPaginated({
        token,
        page: 1,
        pageSize: PAGE_SIZE,
        status: apiStatusFilters.length > 0 ? apiStatusFilters : undefined,
        search: debouncedSearchValue.length >= 3 ? debouncedSearchValue : undefined,
        organizationAccount: effectiveOrgAccount,
        ...dateRangeParams,
      }),
    ).catch((error: unknown) => {
      console.error('Failed to load paginated events:', error);
    });
  }, [isInitialized, debouncedSearchValue, activeFilters, selectedOrgAccount, token]); // eslint-disable-line

  // Infinite scroll: load more when sentinel is visible
  const loadMore = useCallback(() => {
    if (!token) return;
    if (paginatedRequestStatus === 'loading' || paginatedRequestStatus === 'loading-more') return;
    if (paginatedPage >= paginatedTotalPages) return;

    const nextPage = paginatedPage + 1;
    const dateRangeParams = buildDateRangeParams();

    dispatch(
      fetchEventsPaginated({
        token,
        page: nextPage,
        pageSize: PAGE_SIZE,
        status: apiStatusFilters.length > 0 ? apiStatusFilters : undefined,
        search: debouncedSearchValue.length >= 3 ? debouncedSearchValue : undefined,
        organizationAccount: effectiveOrgAccount,
        ...dateRangeParams,
      }),
    ).catch((error: unknown) => {
      console.error('Failed to load more paginated events:', error);
    });
  }, [
    token,
    paginatedRequestStatus,
    paginatedPage,
    paginatedTotalPages,
    apiStatusFilters,
    debouncedSearchValue,
    effectiveOrgAccount,
    buildDateRangeParams,
    dispatch,
  ]);

  // Set up IntersectionObserver on sentinel div
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [loadMore]);

  // Handle search input change
  function onSearchInputChange(value: string) {
    setSearchValue(value);
    updateUrlParams(value, activeFilters, selectedOrgAccount);
  }

  // Handle filter toggle
  function toggleFilter(filter: FilterType) {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveFilters(newFilters);
    updateUrlParams(searchValue, newFilters, selectedOrgAccount);
  }

  // Handle org account filter change
  function onOrgAccountChange(value: string | null) {
    setSelectedOrgAccount(value);
    updateUrlParams(searchValue, activeFilters, value);
  }

  // Update URL params with search, filters, and org_account
  function updateUrlParams(search: string, filters: Set<FilterType>, orgAccount: string | null) {
    const params: Record<string, string> = {};

    if (search.trim()) {
      params.search = search;
    }

    if (filters.size > 0) {
      params.filters = Array.from(filters).join(',');
    }

    if (orgAccount) {
      params.org_account = orgAccount;
    }

    setSearchParams(params);
  }

  // Ensure events is always an array to prevent rendering issues
  const eventsArray = useMemo(() => {
    const eventList = Array.isArray(paginatedEvents) ? paginatedEvents : [];
    return eventList;
  }, [paginatedEvents]);

  // Render search box component
  const renderSearchBox = () => (
    <div className="flex items-center gap-6">
      <div className="grow">
        <div className="flex flex-row gap-2">
          <div className="h-[33px] relative flex-1 rounded-md bg-dataops-supporting-gray/50">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-2 h-5 w-5 text-gray-500"
              aria-hidden="true"
            />
            <Combobox>
              <ComboboxInput
                value={searchValue}
                aria-label="search-events-input"
                placeholder="Type to search events by name or slug"
                className="h-[33px] w-full border border-gray-200 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-500 ring-0 focus:ring-0 sm:text-sm rounded-md outline-none outline-offset-0 focus:outline focus:outline-dataops-primary-light-blue/20 focus:outline-4 transition-all ease-in-out"
                data-testid="search-events-input"
                onChange={(event) => {
                  onSearchInputChange(event.target.value);
                }}
              />
            </Combobox>
          </div>
        </div>
      </div>
    </div>
  );

  // Render filter buttons component
  const renderFilterButtons = () => (
    <div className="flex flex-wrap items-center gap-2">
      {FILTER_CONFIG.map(({ key, label, icon }) => (
        <FilterButton
          key={key}
          label={label}
          icon={icon}
          isActive={activeFilters.has(key)}
          onClick={() => {
            toggleFilter(key);
          }}
        />
      ))}
      {hasMultipleOrgAccounts && (
        <Listbox value={selectedOrgAccount} onChange={onOrgAccountChange}>
          <div className="relative">
            <ListboxButton
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 min-w-[120px]"
              data-testid="org-account-filter"
            >
              <span className="flex-1 text-left truncate">{selectedOrgAccount ?? 'All Accounts'}</span>
              <ChevronUpDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
            </ListboxButton>
            <ListboxOptions className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg text-sm">
              <ListboxOption
                value={null}
                className="px-3 py-1.5 cursor-pointer hover:bg-gray-100 data-[focus]:bg-gray-100"
              >
                All Accounts
              </ListboxOption>
              {snowflakeOrganizationAccounts.map((account) => (
                <ListboxOption
                  key={account}
                  value={account}
                  className="px-3 py-1.5 cursor-pointer hover:bg-gray-100 data-[focus]:bg-gray-100 truncate"
                >
                  {account}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      )}
    </div>
  );

  // Render content based on state
  const renderContent = () => {
    if (paginatedRequestStatus === 'loading') {
      return (
        <div className="flex justify-center py-8">
          <div className="flex items-center gap-2 text-gray-500">
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Loading events...</span>
          </div>
        </div>
      );
    }

    if (paginatedRequestStatus === 'failed') {
      return (
        <div className="text-center py-8">
          <p className="text-red-500">Failed to load events. Please try again.</p>
        </div>
      );
    }

    if (eventsArray.length === 0 && paginatedRequestStatus === 'succeeded') {
      if (debouncedSearchValue.trim()) {
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">No events found matching &ldquo;{debouncedSearchValue}&rdquo;</p>
          </div>
        );
      }
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">No events available</p>
        </div>
      );
    }

    if (eventsArray.length > 0) {
      return (
        <div className="">
          <EventItemHeader />
          <ul role="list" className="w-full space-y-1">
            {eventsArray.map((event) => (
              <li key={event.slug}>
                <Link to={`/event-deployments/${event.slug}`}>
                  <EventItem event={event} />
                </Link>
              </li>
            ))}
          </ul>
          {/* Sentinel div for IntersectionObserver infinite scroll */}
          <div ref={sentinelRef} className="h-1" />
          {/* Loading more indicator */}
          {paginatedRequestStatus === 'loading-more' && (
            <div className="flex justify-center py-4">
              <div className="flex items-center gap-2 text-gray-500">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-sm">Loading more events...</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {renderSearchBox()}
      {renderFilterButtons()}
      {renderContent()}
    </div>
  );
}
